import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/atoms/Button';
import { Typography } from '../ui/atoms/Typography/Typography';
import { type ExamLanguage, PsychometricTest } from '../../types/exam-config.types';
import {
  resolvePsychometricTestDescription,
  resolvePsychometricTestName,
  type JobTFunction,
} from '../../utils/psychometric-test-copy';
import { examLanguageToLocale, getFixedTForExamLanguage } from '@/utils/locale';

interface ImportFromLibraryProps {
  language: ExamLanguage | string;
  allTests: PsychometricTest[];
  attachedTestIds: Set<string>;
  onAdd: (testId: string) => void;
  onRemove?: (testId: string) => void;
  onPreview: (testId: string) => void;
  isAdding: boolean;
  isRemoving?: boolean;
}

export const ImportFromLibrary = ({
  language,
  allTests,
  attachedTestIds,
  onAdd,
  onRemove,
  onPreview,
  isAdding,
  isRemoving = false,
}: ImportFromLibraryProps) => {
  const { t } = useTranslation('job');
  const contentLang = examLanguageToLocale(language);
  const tExam = useMemo<JobTFunction>(
    () => getFixedTForExamLanguage(language),
    [language],
  );
  const hasSelectedTest = attachedTestIds.size > 0;
  const [addingTestId, setAddingTestId] = useState<string | null>(null);
  const [removingTestId, setRemovingTestId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdding) setAddingTestId(null);
  }, [isAdding]);

  useEffect(() => {
    if (!isRemoving) setRemovingTestId(null);
  }, [isRemoving]);

  // Keep all tests visible for clarity, filtered only by language.
  const visibleTests = allTests.filter((test) => test.supported_languages.includes(contentLang));

  if (allTests.length === 0) return null;
  if (visibleTests.length === 0) return null;

  return (
    <div className="space-y-3">
      <Typography
        as="p"
        variant="body-xs"
        weight="semibold"
        color="muted"
        className="uppercase tracking-wider"
      >
        {t('exam_config.library.available_tests')}
      </Typography>
      <div className="space-y-3 pt-1">
        {visibleTests.map((test) => {
          const localizedName = resolvePsychometricTestName(test.slug, test.name, tExam);
          const localizedDescription = resolvePsychometricTestDescription(
            test.slug,
            test.description,
            tExam,
          );
          const [descriptionBodyRaw = '', descriptionNoteRaw] = (localizedDescription ?? '').trim().split('\n\n', 2);
          const descriptionBody = descriptionBodyRaw.trim();
          const descriptionNote = descriptionNoteRaw?.trim() || null;
          const isSelected = attachedTestIds.has(test.id);
          const isDisabledBySelection = hasSelectedTest && !isSelected;

          return (
            <div
              key={test.id}
              className={[
                'relative p-3 rounded-lg border transition-all',
                isSelected
                  ? 'border-secondary ring-1 ring-secondary/25 bg-purple/10'
                  : 'border-dark-invirtus/10 bg-white',
              ].join(' ')}
            >
              <div className="absolute top-3 right-3 z-10 pointer-events-auto flex flex-col items-end gap-1.5 w-44">
                {isSelected && onRemove ? (
                  <Button
                    type="contained"
                    intent="danger"
                    size="sm"
                    label={t('exam_config.library.remove')}
                    className="w-full whitespace-nowrap"
                    onClick={() => {
                      setRemovingTestId(test.id);
                      onRemove(test.id);
                    }}
                    disabled={isRemoving}
                    loading={isRemoving && removingTestId === test.id}
                  />
                ) : (
                  <Button
                    type="contained"
                    intent="secondary"
                    size="sm"
                    label={t('exam_config.library.add')}
                    className="w-full whitespace-nowrap"
                    onClick={() => {
                      setAddingTestId(test.id);
                      onAdd(test.id);
                    }}
                    disabled={isAdding || isDisabledBySelection}
                    loading={isAdding && addingTestId === test.id}
                  />
                )}
                <Button
                  type="outlined"
                  intent="secondary"
                  size="sm"
                  label={t('exam_config.library.preview_questions')}
                  className="w-full whitespace-nowrap"
                  onClick={() => onPreview(test.id)}
                />
              </div>

              <div className={['pr-48', isDisabledBySelection ? 'opacity-45 grayscale-[20%]' : ''].join(' ')}>
                <Typography
                  as="p"
                  variant="body-md"
                  weight="semibold"
                  color="primary"
                  className="leading-5"
                >
                  {localizedName}
                </Typography>
                {localizedDescription && (
                  <div className="mt-1">
                    <Typography as="p" variant="body-sm" color="secondary">
                      {descriptionBody}
                    </Typography>
                    {descriptionNote && (
                      <Typography
                        as="p"
                        variant="body-sm"
                        weight="medium"
                        color="primary"
                        className="mt-1 !leading-5"
                      >
                        {descriptionNote}
                      </Typography>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-1.5 mt-3">
                  <Typography as="span" variant="body-xs" color="muted">
                    {t('exam_config.library.questions_count', { count: test.question_count })}
                  </Typography>
                  <Typography as="span" variant="body-xs" color="muted">
                    &middot;
                  </Typography>
                  <Typography as="span" variant="body-xs" color="muted">
                    {t('exam_config.library.estimated_minutes', { minutes: test.estimated_minutes })}
                  </Typography>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
